import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Params } from '@angular/router';
import * as _ from 'lodash';
import { TreeNode } from 'primeng/api';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { HisArrayUtils, HisStringUtils } from '../../core/utils';
import { HisTreeService } from '../../tree/service/his-tree.service';
import { HisOption } from '../model/his-option';
import { HisSearchConfiguration, HisSearchConfigurationCriterion } from '../model/his-search-configuration';
import { OptionByIdRequest } from '../model/options/option-by-id-request';
import { SelectedOptions } from '../model/options/selected-options';
import { HisSearch, HisSearchCriteria, HisSearchCriteriaGroup, HisSearchGroup } from '../model/search/his-search';
import { HisSearchItem } from '../model/search/his-search-item';
import { HisSearchNonCriteriaUrlParam } from '../model/search/his-search-non-criteria-url-param';
import { HisSearchParser } from '../model/search/his-search-parser';
import { HisSearchParserHelper } from '../model/search/his-search-parser-helper';
import { HisSearchService } from '../service/his-search.service';
import { HisWidgetService } from '../service/his-widget.service';
import { FilterUtils } from '../utils/filter-utils';
import { GenericSearchUtil } from '../utils/generic-search-util';
import { HisQueryParamUtils } from '../utils/his-query-param-utils';

// Suchkriterium
export class HisSearchInfoItem {
    label: string;
    value: string;
    not: string;
    operator: string;
}

// Alle Suchkriterien in einer Kriteriengruppe
export class HisSearchInfoCriteriaGroup {
    excluding: boolean;
    searchCriteriaGroupItems: HisSearchInfoItem[] = [];
}

// Eine Gruppe, z. B. Personendaten, mit allen Kriteriengruppen
export class HisSearchGroupInfo {
    criteriaGroupOr: string;
    searchInfoCriteriaGroup: HisSearchInfoCriteriaGroup[] = [];
}

class LazyCriteriaData {
    configCriterion: HisSearchConfigurationCriterion;
    searchCriteria: HisSearchCriteria;
    isCriteriaGroup: boolean;
    searchInfoCriteriaGroup: HisSearchInfoCriteriaGroup;
    constructor( configCriterion: HisSearchConfigurationCriterion, searchCriteria: HisSearchCriteria, isCriteriaGroup: boolean, searchInfoCriteriaGroup: HisSearchInfoCriteriaGroup ) {
        this.configCriterion = configCriterion;
        this.searchCriteria = searchCriteria;
        this.isCriteriaGroup = isCriteriaGroup;
        this.searchInfoCriteriaGroup = searchInfoCriteriaGroup;
    }
}

@Component( {
    selector: 'his-search-result-search-info',
    templateUrl: './his-search-result-search-info.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisSearchResultSearchInfoComponent implements OnInit {
    private searchService = inject( HisSearchService );
    private hisWidgetService = inject( HisWidgetService );
    private hisUiService = inject( HisUiService );
    private hisTreeService = inject( HisTreeService );


    @Input() searchConfigFile: string;

    @Input() queryParams: Params;

    searchInfo: HisSearchInfoItem[] = [];

    searchGroupInfos: HisSearchGroupInfo[] = [];

    lazySearchCriteriaData: LazyCriteriaData[] = [];

    private search: HisSearch;

    /** maximum number of select options to load */
    private limit = 1000;

    /** constraint on the defaulttext column of that entity */
    private wherePart = '';

    ngOnInit() {
        const isFlatObjectFormat = HisQueryParamUtils.isFlatObjectFormat( this.queryParams );
        this.queryParams = FilterUtils.nullAllFilterParams( this.queryParams );

        if ( this.queryParams ) {

            const searchItems: HisSearchItem[] = [];
            for ( const key of Object.keys( this.queryParams ) ) {
                switch ( key ) {
                    // case HisSearchNonCriteriaUrlParam.CONFIG_FILE: this.configFile = queryParams.configFile; break;
                    case HisSearchNonCriteriaUrlParam.PAGE_TITLE: break;
                    case HisSearchNonCriteriaUrlParam.TABLE_TITLE: break;

                    case HisSearchNonCriteriaUrlParam.IS_NO_SEARCH_ACTIVE:
                        // Falls keine aktive Suche, dann kein params auswerten und "Suchergebnisse" nicht anzeigen.
                        return;

                    case HisSearchNonCriteriaUrlParam.SHOW_CHANGE_SEARCH_BUTTON: break;
                    case HisSearchNonCriteriaUrlParam.SHOW_BACK_BUTTON: break;
                    case HisSearchNonCriteriaUrlParam.PAGE: break;
                    case HisSearchNonCriteriaUrlParam.EXTENDED: break;
                    case HisSearchNonCriteriaUrlParam.NAVIGATION_POSITION: break;
                    // blacklisted keys are ignored, see #192323, #212551, #249099
                    case HisSearchNonCriteriaUrlParam.NO_DB_ACTION:
                    case HisSearchNonCriteriaUrlParam.INIT:
                    case HisSearchNonCriteriaUrlParam.CHOC:
                    case HisSearchNonCriteriaUrlParam.MODE: break;

                    default:
                        // All other parameters are permitted. Keys added by URL manipulation (#223241) are filtered in the backend
                        // Update: key value pairs are now transmitted as part of a flat object
                        // group0.criteria0.field = person.gender.id
                        // group0.criteria0.value = 2
                        if ( !isFlatObjectFormat ) {
                            continue;
                        }
                        searchItems.push( new HisSearchItem( key, this.queryParams[key] ) );
                }
            }
            if ( isFlatObjectFormat ) {
                this.search = HisSearchParser.decode( searchItems );
                this.search.configFile = this.searchConfigFile;
            } else {
                this.search = HisSearchParserHelper.convertUrlParamsToHisSearch( this.queryParams );
            }
        }
        if ( !this.searchConfigFile ) {
            return;
        }
        this.searchService.loadSearchConfiguration( this.searchConfigFile ).subscribe( ( searchConfig: HisSearchConfiguration ) => {
            if ( searchConfig ) {

                this.hisWidgetService.loadWidgetOptions( searchConfig, true );

                for ( const searchGroup of this.search.groups ) {

                    const searchGroupInfo = new HisSearchGroupInfo();
                    if ( searchGroup.criteriaGroupAnd === false ) {
                        searchGroupInfo.criteriaGroupOr = this.hisUiService.translate( 'genericSearch.result.orLabel' );
                    }

                    this.addItems( searchGroup, searchConfig );

                    this.addCriteriaGroupItems( searchGroup, searchConfig, searchGroupInfo );

                    if ( !HisArrayUtils.isEmptyArray( searchGroupInfo.searchInfoCriteriaGroup ) ) {
                        this.searchGroupInfos.push( searchGroupInfo );
                    }
                }
                this.handleLazyCriterias( searchConfig.configFile );
            }
        } );
    }

    private handleLazyCriterias( configFile: string ) {
        if ( this.lazySearchCriteriaData.length == 0 ) {
            return;
        }
        const optionByIdRequests: OptionByIdRequest[] = [];
        for ( const lazyCriteriaData of this.lazySearchCriteriaData ) {
            optionByIdRequests.push( new OptionByIdRequest( lazyCriteriaData.configCriterion.selectFrom, [lazyCriteriaData.searchCriteria.value] ) );
        }

        this.searchService.loadOptionsByIds( configFile, optionByIdRequests ).subscribe(
            ( result: SelectedOptions[] ) => {

                if ( !result ) {
                    return;
                }

                for ( let selectedOption of result ) {
                    if ( !selectedOption || !selectedOption.selectedOptions || selectedOption.selectedOptions.length == 0 ) {
                        continue;
                    }
                    const matchingCriteriaData = this.lazySearchCriteriaData.find( criteriaData => criteriaData.searchCriteria.value === selectedOption.selectedOptions[0].id );
                    this.addToSearchInfo( matchingCriteriaData.configCriterion,
                        selectedOption.selectedOptions[0].label,
                        matchingCriteriaData.searchCriteria.not,
                        matchingCriteriaData.searchCriteria.operator,
                        matchingCriteriaData.isCriteriaGroup,
                        matchingCriteriaData.searchInfoCriteriaGroup );
                }
            }
        );
    }

    private addItems( searchGroup: HisSearchGroup, searchConfig: HisSearchConfiguration ) {
        for ( const searchCriterion of searchGroup.criteria ) {
            this.handleSearchFields( searchCriterion, searchConfig, false, null );
        }
    }

    private addCriteriaGroupItem( criteriaGroup: HisSearchCriteriaGroup, searchConfig: HisSearchConfiguration, searchInfoCriteriaGroup: HisSearchInfoCriteriaGroup ) {
        for ( const searchCriterion of criteriaGroup.criteria ) {
            this.handleSearchFields( searchCriterion, searchConfig, true, searchInfoCriteriaGroup );
        }
    }

    private handleSearchFields( criteria: HisSearchCriteria, searchConfig: HisSearchConfiguration, isCriteriaGroup: boolean, searchInfoCriteriaGroup: HisSearchInfoCriteriaGroup ) {
        if ( HisStringUtils.isBlank( criteria.value ) ) {
            return;
        }

        const configCriterion: HisSearchConfigurationCriterion = this.getConfigCriterionFromSearchCriterion( criteria, searchConfig );
        if ( !configCriterion ) {
            return;
        }

        const value = criteria.value;
        const not = criteria.not;
        const operator = criteria.operator;

        if ( configCriterion.selectFrom && !configCriterion.multipleSelect ) {
            if ( GenericSearchUtil.isLazySelectFrom( configCriterion.selectFrom, configCriterion.showDropdownIcon ) ) {
                this.lazySearchCriteriaData.push( new LazyCriteriaData( configCriterion, criteria, isCriteriaGroup, searchInfoCriteriaGroup ) );
            } else {
                this.handleSingleSelect( configCriterion, value, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
            }
            return;
        }

        if ( configCriterion.selectFrom && configCriterion.multipleSelect ) {
            this.handleMultiSelect( configCriterion, value, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
            return;
        }

        if ( !_.isNil( configCriterion.value ) ) {
            this.handleCheckbox( configCriterion, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
            return;
        }

        if ( configCriterion.selectFromTree ) {
            this.handleTreeSelect( configCriterion, value, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
            return;
        }

        if ( configCriterion.searchConfigFile ) {
            this.handleSearchSelect( configCriterion, value, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
            return;
        }

        // input, date
        this.addToSearchInfo( configCriterion, criteria.value, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
    }

    private handleSingleSelect( configCriterion: HisSearchConfigurationCriterion, value: string, not: boolean, operator: string, isCriteriaGroup: boolean, searchInfoCriteriaGroup: HisSearchInfoCriteriaGroup ): void {
        this.hisWidgetService.getSelectOptions( configCriterion.selectFrom ).subscribe( ( options: HisOption[] ) => {
            
            if ( options?.length ) {
                // option.id z. B. 5
                // option.label, z. B. männlich
                const option: HisOption = this.getOptionWithValue( value, options );
                if ( option ) {
                    const label = option.label;
                    this.addToSearchInfo( configCriterion, label, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
                    return;
                }
                    
                if ( value === 'ISNULL' ) {
                    const nullValueLabel = this.hisUiService.translate( 'genericSearch.nullValueMenuEntry' );
                    this.addToSearchInfo( configCriterion, nullValueLabel, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
                }
            }
        } );
    }

    private handleMultiSelect( configCriterion: HisSearchConfigurationCriterion, value: string, not: boolean, operator: string, isCriteriaGroup: boolean, searchInfoCriteriaGroup: HisSearchInfoCriteriaGroup ): void {
        this.hisWidgetService.getSelectOptions( configCriterion.selectFrom ).subscribe( ( options: HisOption[] ) => {

            if ( options?.length ) {
                const valueArray: string[] = value.split( ',' );
                const optionValue = this.getOptionValues( valueArray, options );
                this.addToSearchInfo( configCriterion, optionValue, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
            }
        } );
    }

    private handleCheckbox( configCriterion: HisSearchConfigurationCriterion, not: boolean, operator: string, isCriteriaGroup: boolean, searchInfoCriteriaGroup: HisSearchInfoCriteriaGroup ): void {
        const yesValue = this.hisUiService.translate( 'de.his.javang.ja' );
        this.addToSearchInfo( configCriterion, yesValue, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
    }

    private handleTreeSelect( configCriterion: HisSearchConfigurationCriterion, value: string, not: boolean, operator: string, isCriteriaGroup: boolean, searchInfoCriteriaGroup: HisSearchInfoCriteriaGroup ): void {
        // treeSelect
        this.hisTreeService.loadTree( configCriterion.treeName, configCriterion.selectFromTree )
            .subscribe( ( nodes: TreeNode[] ) => {
                if ( nodes ) {
                    const isLidSearch = GenericSearchUtil.isLidPath( configCriterion.path );
                    const nodeLabels = this.getNodeLabels( nodes, value, isLidSearch );
                    this.addToSearchInfo( configCriterion, nodeLabels, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
                }
            } );
    }

    private handleSearchSelect( configCriterion: HisSearchConfigurationCriterion, value: string, not: boolean, operator: string, isCriteriaGroup: boolean, searchInfoCriteriaGroup: HisSearchInfoCriteriaGroup ): void {
        const subSearchSearchConfigFile = configCriterion.searchConfigFile;
        this.searchService.loadFilteredOptions( subSearchSearchConfigFile, this.wherePart, this.limit )
            .subscribe(
                ( options: HisOption[] ) => {
                    if ( options ) {
                        const valueArray: string[] = value.split( ',' );
                        const optionValue = this.getOptionValues( valueArray, options );
                        this.addToSearchInfo( configCriterion, optionValue, not, operator, isCriteriaGroup, searchInfoCriteriaGroup );
                    }
                }
            );
    }

    private addCriteriaGroupItems( searchGroup: HisSearchGroup, searchConfig: HisSearchConfiguration, searchGroupInfo: HisSearchGroupInfo ) {
        for ( const criteriaGroup of searchGroup.criteriaGroups ) {

            const searchInfoCriteriaGroup = new HisSearchInfoCriteriaGroup();

            searchInfoCriteriaGroup.excluding = criteriaGroup.excluding;

            this.addCriteriaGroupItem( criteriaGroup, searchConfig, searchInfoCriteriaGroup );

            if ( !HisArrayUtils.isEmptyArray( searchInfoCriteriaGroup ) ) {
                searchGroupInfo.searchInfoCriteriaGroup.push( searchInfoCriteriaGroup );
            }
        }
    }

    private addToSearchInfo( configCriterion: HisSearchConfigurationCriterion, label: string | string[], not: boolean, operator: string, isCriteriaGroup: boolean, searchInfoCriteriaGroup: HisSearchInfoCriteriaGroup ) {
        const searchInfoItem = this.createSearchInfoItem( configCriterion.label, label, not, operator );
        if ( !searchInfoItem ) {
            return;
        }

        if ( isCriteriaGroup ) {
            searchInfoCriteriaGroup.searchCriteriaGroupItems.push( searchInfoItem );
            return;
        }

        this.searchInfo.push( searchInfoItem );
    }

    private getNodeLabels( nodes: TreeNode[], value: string, isLidSearch: boolean ): string[] {
        return nodes.flatMap( node => this.getNodeLabelForOneNode( node, value, isLidSearch ) );
    }

    private getNodeLabelForOneNode( node: TreeNode, value: string, isLidSearch: boolean ): string[] {
        const idOrLidValues: string[] = value.split( ',' );
        const nodeLabels = idOrLidValues
            .map( idOrLidValue => this.getNodeLabel( node, idOrLidValue, isLidSearch ) )
            .filter( nodeLabel => nodeLabel != null );

        return nodeLabels;
    }

    private getNodeLabel( node: TreeNode, idOrLid: string, isLidSearch: boolean ): string | null {
        const includeSubNodes = GenericSearchUtil.isIdOrLidTreeSelectionIncludingSubnodes( idOrLid );
        const cleanIdOrLid = GenericSearchUtil.idOrLidTreeSelectionToCleanIdOrLid( idOrLid );

        // TODO das ist eine komische Bedingung, die ich nicht verstehe
        if ( cleanIdOrLid === 0 ) {
            return null;
        }

        const foundNode = this.findTreeNodeWithIdOrLid( node, cleanIdOrLid, isLidSearch );
        if ( !foundNode ) {
            return null;
        }

        const data = foundNode.data;
        if ( includeSubNodes ) {
            return data.defaulttext + ' (' + this.hisUiService.translate( 'genericSearch.tree.includeSubNodes' ) + ')';
        }

        return data.defaulttext;
    }

    /**
     * @param labelAsI18nKey
     * @param value
     * @param criterion
     */
    private createSearchInfoItem(
        labelAsI18nKey: string,
        label: string | string[],
        not: boolean,
        operator: string ) {

        if ( !label ) {
            return null;
        }

        const searchInfoItem = new HisSearchInfoItem();

        searchInfoItem.label = this.hisUiService.translate( labelAsI18nKey ) + ': ';

        if ( typeof label == 'string' ) {
            searchInfoItem.value = label + '; ';
        } else {
            searchInfoItem.value = label.join( ', ' ) + '; ';
            if ( not ) {
                searchInfoItem.value = '(' + label.join( ', ' ) + ')' + '; ';
            }
        }

        if ( not ) {
            searchInfoItem.not = this.hisUiService.translate( 'genericSearch.result.notLabel' );
        }

        if ( operator ) {
            searchInfoItem.operator = operator;
        }

        return searchInfoItem;
    }

    private getConfigCriterionFromSearchCriterion( searchCriterion: HisSearchCriteria, searchConfig: HisSearchConfiguration ): HisSearchConfigurationCriterion {
        for ( const group of searchConfig.searchCriteria.group ) {
            for ( const criterion of group.criteria ) {
                if ( criterion.path === searchCriterion.field ) {
                    return criterion;
                }
            }
        }
        return null;
    }

    private getOptionValues( value: string[], options: HisOption[] ): string[] {
        const optionValues: string[] = [];
        for ( const val of value ) {
            const option = this.getOptionWithValue( val, options );
            if ( option && option.label ) {
                optionValues.push( option.label );
            }
        }
        return optionValues;
    }

    private getOptionWithValue( value: string, options: HisOption[] ): HisOption {
        for ( const option of options ) {
            if ( option.id === value ) {
                return option;
            }
        }
        return null;
    }

    private findTreeNodeWithIdOrLid( treeNode: TreeNode, idOrLid: number, isLidSearch: boolean ): TreeNode {
        if ( isLidSearch && treeNode.data.lid === idOrLid ) {
            return treeNode;
        }
        if ( !isLidSearch && treeNode.data.id === idOrLid ) {
            return treeNode;
        }
        let child: TreeNode;
        for ( child of treeNode.children ) {
            const foundNode: TreeNode = this.findTreeNodeWithIdOrLid( child, idOrLid, isLidSearch );
            if ( foundNode ) {
                return foundNode;
            }
        }
        return null;
    }

}
